import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export type TutorialStep = 
  | "first_search" 
  | "copy_citation" 
  | "second_search" 
  | "view_history" 
  | "generate_summary" 
  | "word_addin";

interface TutorialProgress {
  step_first_search: boolean;
  step_copy_citation: boolean;
  step_second_search: boolean;
  step_view_history: boolean;
  step_generate_summary: boolean;
  step_word_addin: boolean;
  tutorial_completed: boolean;
  tutorial_dismissed: boolean;
}

interface TutorialContextType {
  isEligible: boolean;
  isLoading: boolean;
  progress: TutorialProgress | null;
  currentStep: TutorialStep | null;
  completedStepsCount: number;
  totalSteps: number;
  completeStep: (step: TutorialStep) => Promise<void>;
  dismissTutorial: () => Promise<void>;
  showSemanticSearchCallout: boolean;
  setShowSemanticSearchCallout: (show: boolean) => void;
}

const defaultProgress: TutorialProgress = {
  step_first_search: false,
  step_copy_citation: false,
  step_second_search: false,
  step_view_history: false,
  step_generate_summary: false,
  step_word_addin: false,
  tutorial_completed: false,
  tutorial_dismissed: false,
};

const TutorialContext = createContext<TutorialContextType | null>(null);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const { user, subscription } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isEligible, setIsEligible] = useState(false);
  const [progress, setProgress] = useState<TutorialProgress | null>(null);
  const [showSemanticSearchCallout, setShowSemanticSearchCallout] = useState(false);

  const totalSteps = 6;

  // Calculate current step based on progress
  const currentStep = ((): TutorialStep | null => {
    if (!progress || progress.tutorial_completed || progress.tutorial_dismissed) return null;
    if (!progress.step_first_search) return "first_search" as TutorialStep;
    if (!progress.step_copy_citation) return "copy_citation" as TutorialStep;
    if (!progress.step_second_search) return "second_search" as TutorialStep;
    if (!progress.step_view_history) return "view_history" as TutorialStep;
    if (!progress.step_generate_summary) return "generate_summary" as TutorialStep;
    if (!progress.step_word_addin) return "word_addin" as TutorialStep;
    return null;
  })();

  const completedStepsCount = progress
    ? [
        progress.step_first_search,
        progress.step_copy_citation,
        progress.step_second_search,
        progress.step_view_history,
        progress.step_generate_summary,
        progress.step_word_addin,
      ].filter(Boolean).length
    : 0;

  // Check eligibility based on user created_at or trial_reset_2026
  const checkEligibility = useCallback(async () => {
    if (!user) {
      setIsEligible(false);
      setIsLoading(false);
      return;
    }

    // Check if user was created in the last 24 hours
    const userCreatedAt = new Date(user.created_at);
    const now = new Date();
    const hoursSinceCreation = (now.getTime() - userCreatedAt.getTime()) / (1000 * 60 * 60);
    const isNewUser = hoursSinceCreation < 24;

    // Check if user has trial_reset_2026 flag
    const hasTrialReset = subscription?.trial_reset_2026 === true;

    setIsEligible(isNewUser || hasTrialReset);
  }, [user, subscription]);

  // Fetch or create tutorial progress
  const fetchProgress = useCallback(async () => {
    if (!user) {
      setProgress(null);
      setIsLoading(false);
      return;
    }

    try {
      // First try to fetch existing progress
      const { data, error } = await supabase
        .from("user_tutorial_progress")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching tutorial progress:", error);
        setIsLoading(false);
        return;
      }

      if (data) {
        setProgress({
          step_first_search: data.step_first_search,
          step_copy_citation: data.step_copy_citation,
          step_second_search: data.step_second_search,
          step_view_history: data.step_view_history,
          step_generate_summary: data.step_generate_summary,
          step_word_addin: data.step_word_addin,
          tutorial_completed: data.tutorial_completed,
          tutorial_dismissed: data.tutorial_dismissed,
        });
      } else if (isEligible) {
        // Create new progress record for eligible users
        const { error: insertError } = await supabase
          .from("user_tutorial_progress")
          .insert({ user_id: user.id });

        if (insertError) {
          console.error("Error creating tutorial progress:", insertError);
        } else {
          setProgress(defaultProgress);
        }
      }
    } catch (err) {
      console.error("Error in fetchProgress:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user, isEligible]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  useEffect(() => {
    if (isEligible !== undefined) {
      fetchProgress();
    }
  }, [fetchProgress, isEligible]);

  const fireConfetti = (intensity: "light" | "medium" | "intense" = "medium") => {
    const count = intensity === "light" ? 50 : intensity === "medium" ? 100 : 200;
    const spread = intensity === "light" ? 50 : intensity === "medium" ? 70 : 100;

    confetti({
      particleCount: count,
      spread: spread,
      origin: { y: 0.7 },
      colors: ["#7c3aed", "#a855f7", "#c084fc", "#e879f9"],
    });
  };

  const getStepMessage = (step: TutorialStep): { title: string; description: string } => {
    switch (step) {
      case "first_search":
        return {
          title: "🎉 ¡Primera búsqueda completada!",
          description: "Descubriste el poder de la búsqueda semántica con IA.",
        };
      case "copy_citation":
        return {
          title: "📋 ¡Cita copiada!",
          description: "Ahora podés pegar la cita directamente en tus escritos.",
        };
      case "second_search":
        return {
          title: "🔍 ¡Segunda búsqueda completada!",
          description: "Ya dominás las búsquedas con lenguaje natural.",
        };
      case "view_history":
        return {
          title: "📚 ¡Historial explorado!",
          description: "Tus búsquedas quedan guardadas para acceder cuando quieras.",
        };
      case "generate_summary":
        return {
          title: "🧠 ¡Resumen generado!",
          description: "La IA resumió el fallo en segundos. ¡Impresionante!",
        };
      case "word_addin":
        return {
          title: "🎊 ¡Tutorial completado!",
          description: "¡Felicitaciones! Ya conocés todas las funciones de DoctIA.",
        };
    }
  };

  const completeStep = useCallback(
    async (step: TutorialStep) => {
      if (!user || !progress) return;

      const stepKey = `step_${step}` as keyof TutorialProgress;
      if (progress[stepKey]) return; // Already completed

      const newProgress = { ...progress, [stepKey]: true };

      // Check if all steps are completed
      const allStepsCompleted =
        newProgress.step_first_search &&
        newProgress.step_copy_citation &&
        newProgress.step_second_search &&
        newProgress.step_view_history &&
        newProgress.step_generate_summary &&
        newProgress.step_word_addin;

      if (allStepsCompleted) {
        newProgress.tutorial_completed = true;
      }

      try {
        const updatePayload = {
          [stepKey]: true,
          tutorial_completed: allStepsCompleted,
          updated_at: new Date().toISOString(),
        } as never;
        const { error } = await supabase
          .from("user_tutorial_progress")
          .update(updatePayload)
          .eq("user_id", user.id);

        if (error) {
          console.error("Error updating tutorial progress:", error);
          return;
        }

        setProgress(newProgress);

        // Show celebration
        const message = getStepMessage(step);
        const intensity = step === "word_addin" ? "intense" : "medium";
        fireConfetti(intensity);

        toast.success(message.title, {
          description: message.description,
          duration: 4000,
        });

        // Show semantic search callout after first search
        if (step === "first_search") {
          setShowSemanticSearchCallout(true);
        }
      } catch (err) {
        console.error("Error completing step:", err);
      }
    },
    [user, progress]
  );

  const dismissTutorial = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_tutorial_progress")
        .update({
          tutorial_dismissed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id);

      if (error) {
        console.error("Error dismissing tutorial:", error);
        return;
      }

      setProgress((prev) => (prev ? { ...prev, tutorial_dismissed: true } : null));

      toast.info("Tutorial omitido", {
        description: "Podés acceder a la ayuda desde el menú cuando quieras.",
      });
    } catch (err) {
      console.error("Error dismissing tutorial:", err);
    }
  }, [user]);

  return (
    <TutorialContext.Provider
      value={{
        isEligible,
        isLoading,
        progress,
        currentStep,
        completedStepsCount,
        totalSteps,
        completeStep,
        dismissTutorial,
        showSemanticSearchCallout,
        setShowSemanticSearchCallout,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
}
